import { useParams, Link } from 'react-router-dom';
import { articles, getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import SEOMeta from '@/components/feature/SEOMeta';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = getRelatedArticles(id || '', 3);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonical = `${origin}/article/${id}`;

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: origin,
      },
      ...(category
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `${origin}/category/${article.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
              item: canonical,
            },
          ]
        : []),
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorTitle,
    },
    publisher: {
      '@type': 'Organization',
      name: '黄瓜传媒',
      logo: {
        '@type': 'ImageObject',
        url: `${origin}/vite.svg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    articleSection: article.category,
    keywords: article.tags.join(', '),
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.excerpt,
    url: canonical,
    isPartOf: {
      '@type': 'WebSite',
      name: '黄瓜传媒',
      url: origin,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOMeta
        title={`${article.title} - 黄瓜传媒`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonical={canonical}
      />
      <SchemaOrg schema={breadcrumbSchema} />
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={webpageSchema} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-6 md:pt-8 pb-4">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              ...(category
                ? [
                    {
                      label: category.name,
                      href: `/category/${article.categorySlug}`,
                    },
                  ]
                : []),
              { label: article.title },
            ]}
          />
        </div>
      </div>

      {/* Hero image */}
      <div className="w-full px-4 md:px-6 lg:px-10 mb-6 md:mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Article content */}
      <article className="w-full px-4 md:px-6 lg:px-10 pb-12 md:pb-16">
        <div className="max-w-3xl mx-auto">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-500">
            <span className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 font-medium text-xs">
              {article.category}
            </span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}阅读</span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-6 leading-tight">
            {article.title}
          </h1>

          {/* Author */}
          <div className="flex items-center gap-3 mb-8 pb-8 border-b border-background-200/70">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold">
              {article.author.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-900">{article.author}</p>
              <p className="text-xs text-foreground-500">{article.authorTitle}</p>
            </div>
          </div>

          {/* Content */}
          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-200/70 text-foreground-600 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6 md:mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((ra) => (
                <article
                  key={ra.id}
                  className="group flex flex-col bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/60 hover:shadow-lg transition-all duration-300"
                >
                  <Link to={`/article/${ra.id}`} className="relative aspect-[16/10] overflow-hidden flex-shrink-0">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <div className="flex flex-col flex-1 p-5">
                    <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      <Link to={`/article/${ra.id}`}>{ra.title}</Link>
                    </h3>
                    <p className="text-xs text-foreground-500">{ra.date}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}